import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import BackButton from "@/components/BackButton";
import {
  ShoppingBag,
  Coins,
  Sparkles,
  CheckCircle2,
  Star,
  Package,
} from "lucide-react";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "books", label: "Books" },
  { key: "hardware", label: "Hardware" },
  { key: "software", label: "Software" },
  { key: "subscriptions", label: "Subscriptions" },
  { key: "apparel", label: "Apparel" },
  { key: "stationery", label: "Stationery" },
  { key: "events", label: "Events" },
  { key: "coaching", label: "Coaching" },
];

export default function StorePage() {
  const { user, refresh } = useAuth();
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [currency, setCurrency] = useState("mwz");
  const [qty, setQty] = useState(1);
  const [orders, setOrders] = useState([]);
  const [showOrders, setShowOrders] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, ord] = await Promise.all([
        api.get("/store/products"),
        api.get("/store/orders/me"),
      ]);
      setProducts(data);
      setOrders(ord.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () => (tab === "all" ? products : products.filter((p) => p.category === tab)),
    [tab, products]
  );

  const featured = useMemo(() => products.filter((p) => p.featured).slice(0, 3), [products]);

  const checkout = async () => {
    if (!selected) return;
    try {
      const { data } = await api.post("/store/checkout", {
        product_id: selected.id,
        quantity: qty,
        currency,
      });
      toast.success(`Order placed · ${selected.name}`);
      setSelected(null);
      setQty(1);
      refresh();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
    }
  };

  return (
    <div className="p-6 md:p-10">
      <BackButton />
      <div className="mt-5 mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            /online store
          </div>
          <h1 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
            Growthink Marketplace
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Books, hardware, software credits, apparel, events & coaching — pay with Rand or your <span className="font-semibold text-foreground">Mowanzi (MWZ)</span> coins earned through learning.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="border border-foreground bg-background p-3" data-testid="store-balance">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Your balance
            </div>
            <div className="mt-1 flex items-center gap-3 text-sm">
              <span className="font-mono font-semibold">R{(user?.wallet_balance || 0).toFixed(2)}</span>
              <span className="text-muted-foreground">·</span>
              <span className="flex items-center gap-1 font-mono font-semibold text-primary">
                <Coins className="h-3.5 w-3.5" />
                {(user?.mwz_balance || 0).toLocaleString()} MWZ
              </span>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowOrders(true)}
            className="rounded-none border-foreground hard-shadow-sm"
            data-testid="my-orders-btn"
          >
            <Package className="mr-2 h-4 w-4" /> My orders ({orders.length})
          </Button>
        </div>
      </div>

      {/* Featured strip */}
      {featured.length > 0 && (
        <div className="mb-8 border-2 border-foreground bg-foreground p-5 text-background">
          <div className="mb-3 flex items-center justify-between">
            <div className="font-display text-lg font-extrabold tracking-tight">
              <Star className="mr-1 inline h-4 w-4 text-accent" /> Featured this week
            </div>
            <div className="mono text-[10px] uppercase tracking-widest text-background/60">
              Top picks
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {featured.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                className="group flex gap-3 border border-background/20 bg-background p-3 text-left text-foreground transition-transform hover:translate-x-[-2px] hover:translate-y-[-2px]"
                data-testid={`featured-${p.id}`}
              >
                <img src={p.image_url} alt={p.name} className="h-14 w-14 shrink-0 border border-foreground object-cover" />
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-1 text-sm font-semibold">{p.name}</div>
                  <div className="mono mt-1 flex items-center gap-1 text-[10px] text-primary">
                    <Coins className="h-3 w-3" />
                    {p.price_mwz.toLocaleString()} MWZ
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="flex h-auto flex-wrap rounded-none border border-foreground bg-background p-0">
          {CATEGORIES.map((c) => (
            <TabsTrigger
              key={c.key}
              value={c.key}
              className="rounded-none border-r border-foreground/20 px-3 py-2 mono text-[10px] uppercase tracking-widest last:border-r-0 data-[state=active]:bg-foreground data-[state=active]:text-background"
              data-testid={`store-tab-${c.key}`}
            >
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-80 animate-pulse border border-border bg-muted" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="flex flex-col border-2 border-foreground bg-background overflow-hidden hover-lift"
              data-testid={`product-card-${p.id}`}
            >
              <div className="relative aspect-square overflow-hidden bg-muted">
                <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
                <div className="absolute left-2 top-2 mono bg-foreground px-2 py-0.5 text-[10px] uppercase tracking-widest text-background">
                  {p.category}
                </div>
                {p.featured && (
                  <div className="absolute right-2 top-2 mono bg-accent px-2 py-0.5 text-[10px] uppercase tracking-widest text-accent-foreground">
                    Featured
                  </div>
                )}
              </div>
              <div className="flex flex-1 flex-col p-4">
                <div className="font-display text-base font-extrabold leading-tight tracking-tight">
                  {p.name}
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                  {p.short_description}
                </p>
                <div className="mt-auto pt-4">
                  <div className="flex items-baseline justify-between">
                    <div className="flex flex-col">
                      <div className="font-display text-lg font-extrabold tracking-tight">
                        R{p.price_zar.toFixed(0)}
                      </div>
                      <div className="mono flex items-center gap-1 text-[10px] text-primary">
                        <Coins className="h-3 w-3" />
                        {p.price_mwz.toLocaleString()} MWZ
                      </div>
                    </div>
                    <Button
                      onClick={() => setSelected(p)}
                      className="rounded-none bg-foreground text-background hard-shadow-sm"
                      data-testid={`buy-${p.id}`}
                    >
                      <ShoppingBag className="mr-1 h-3.5 w-3.5" /> Buy
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Checkout dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg rounded-none border-2 border-foreground p-0 hard-shadow" data-testid="checkout-dialog">
          {selected && (
            <>
              <DialogHeader className="border-b border-border bg-foreground p-5 text-background">
                <DialogTitle className="font-display text-lg font-extrabold tracking-tight text-background">
                  Checkout
                </DialogTitle>
              </DialogHeader>
              <div className="p-5">
                <div className="flex gap-4">
                  <img src={selected.image_url} alt={selected.name} className="h-24 w-24 border border-foreground object-cover" />
                  <div className="flex-1">
                    <div className="font-display text-lg font-extrabold tracking-tight">
                      {selected.name}
                    </div>
                    <div className="mono mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                      {selected.category}
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {selected.short_description}
                    </p>
                  </div>
                </div>

                <div className="mt-5 border-t border-border pt-4">
                  <div className="mono mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                    Pay with
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setCurrency("mwz")}
                      className={`border p-3 text-left ${currency === "mwz" ? "border-foreground bg-foreground text-background hard-shadow-sm" : "border-border"}`}
                      data-testid="currency-mwz"
                    >
                      <div className="mono text-[10px] uppercase tracking-widest opacity-70">
                        Mowanzi Coins
                      </div>
                      <div className="font-display mt-1 flex items-center gap-1 text-lg font-extrabold">
                        <Coins className="h-4 w-4" />
                        {(selected.price_mwz * qty).toLocaleString()} MWZ
                      </div>
                    </button>
                    <button
                      onClick={() => setCurrency("zar")}
                      className={`border p-3 text-left ${currency === "zar" ? "border-foreground bg-foreground text-background hard-shadow-sm" : "border-border"}`}
                      data-testid="currency-zar"
                    >
                      <div className="mono text-[10px] uppercase tracking-widest opacity-70">
                        Rand (ZAR)
                      </div>
                      <div className="font-display mt-1 text-lg font-extrabold">
                        R{(selected.price_zar * qty).toFixed(2)}
                      </div>
                    </button>
                  </div>

                  <div className="mt-4 flex items-center gap-3">
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      Quantity
                    </div>
                    <div className="flex items-center">
                      <button
                        onClick={() => setQty(Math.max(1, qty - 1))}
                        className="border border-foreground bg-background px-3 py-1 hover:bg-foreground hover:text-background"
                      >
                        −
                      </button>
                      <div className="border-y border-foreground bg-background px-4 py-1 mono text-sm">{qty}</div>
                      <button
                        onClick={() => setQty(qty + 1)}
                        className="border border-foreground bg-background px-3 py-1 hover:bg-foreground hover:text-background"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                  <Button variant="ghost" className="rounded-none" onClick={() => setSelected(null)} data-testid="checkout-cancel">
                    Cancel
                  </Button>
                  <Button
                    onClick={checkout}
                    className="rounded-none bg-foreground text-background hard-shadow-sm"
                    data-testid="checkout-confirm"
                  >
                    Confirm purchase →
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Orders dialog */}
      <Dialog open={showOrders} onOpenChange={setShowOrders}>
        <DialogContent className="max-w-xl rounded-none border-2 border-foreground p-0 hard-shadow">
          <DialogHeader className="border-b border-border bg-foreground p-5 text-background">
            <DialogTitle className="font-display text-lg font-extrabold tracking-tight text-background">
              My orders
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto p-5">
            {orders.length === 0 ? (
              <div className="border border-dashed border-foreground/30 p-6 text-center text-sm text-muted-foreground">
                No orders yet. Earn MWZ and treat yourself!
              </div>
            ) : (
              <div className="space-y-2">
                {orders.map((o) => (
                  <div key={o.id} className="flex items-center justify-between border border-border p-3">
                    <div>
                      <div className="text-sm font-semibold">{o.product_name}</div>
                      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        {new Date(o.created_at).toLocaleString()} · qty {o.quantity}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-semibold">
                        {o.currency === "mwz" ? `${o.total.toLocaleString()} MWZ` : `R${o.total.toFixed(2)}`}
                      </div>
                      <div className="mono mt-0.5 inline-flex items-center gap-1 bg-accent px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-accent-foreground">
                        <CheckCircle2 className="h-2.5 w-2.5" /> {o.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
